/**
 * useSearchHistorySync - Hook for syncing search history across devices
 * Syncs localStorage searches with server when user is logged in
 */
import { useState, useEffect, useCallback } from 'react';
import { getRecentSearches, saveRecentSearch, clearRecentSearches } from '../components/RecentSearches';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function useSearchHistorySync() {
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  
  // Check if user is logged in
  useEffect(() => {
    const token = localStorage.getItem('token');
    setIsLoggedIn(!!token);
  }, []);
  
  // Get auth header
  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };
  
  // Sync local history to server
  const syncToServer = useCallback(async () => {
    if (!isLoggedIn) return { success: false, reason: 'Not logged in' };
    
    setIsSyncing(true);
    try {
      const localSearches = getRecentSearches();
      
      if (localSearches.length === 0) {
        setIsSyncing(false);
        return { success: true, synced: 0 };
      }
      
      const response = await fetch(`${API_URL}/api/search-history/sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        },
        body: JSON.stringify({
          items: localSearches.map(s => ({
            filters: s.filters,
            category: 'property',
            result_count: s.resultCount || 0,
            display_text: s.displayText,
            country: s.filters?.country || 'LK'
          }))
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setLastSyncTime(new Date().toISOString());
        localStorage.setItem('lastSearchHistorySync', new Date().toISOString());
        return { success: true, synced: data.synced_count, items: data.items };
      } else if (response.status === 401) {
        setIsLoggedIn(false);
        return { success: false, reason: 'Not logged in' };
      }
      
      return { success: false, reason: 'Sync failed' };
    } catch (error) {
      console.error('Sync error:', error);
      return { success: false, reason: error.message };
    } finally {
      setIsSyncing(false);
    }
  }, [isLoggedIn]);
  
  // Fetch history from server
  const fetchFromServer = useCallback(async (limit = 10) => {
    if (!isLoggedIn) return { success: false, items: [] };
    
    try {
      const response = await fetch(`${API_URL}/api/search-history/?limit=${limit}`, {
        headers: getAuthHeader()
      });
      
      if (response.ok) {
        const data = await response.json();
        return { success: true, items: data.items };
      } else if (response.status === 401) {
        setIsLoggedIn(false);
      }
      
      return { success: false, items: [] };
    } catch (error) {
      console.error('Fetch error:', error);
      return { success: false, items: [] };
    }
  }, [isLoggedIn]);
  
  // Add a search to history (both local and server if logged in)
  const addSearch = useCallback(async (filters, resultCount = 0) => {
    // Always save locally
    const localEntry = saveRecentSearch(filters, resultCount);
    
    // If logged in, also save to server
    if (isLoggedIn) {
      try {
        await fetch(`${API_URL}/api/search-history/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeader()
          },
          body: JSON.stringify({
            filters,
            category: 'property',
            result_count: resultCount,
            display_text: localEntry?.displayText,
            country: filters?.country || 'LK'
          })
        });
      } catch (error) {
        console.error('Failed to save search to server:', error);
      }
    }
    
    return localEntry;
  }, [isLoggedIn]);
  
  // Clear all history (both local and server)
  const clearAll = useCallback(async () => {
    clearRecentSearches();
    
    if (isLoggedIn) {
      try {
        await fetch(`${API_URL}/api/search-history/`, {
          method: 'DELETE',
          headers: getAuthHeader()
        });
      } catch (error) {
        console.error('Failed to clear server history:', error);
      }
    }
  }, [isLoggedIn]);
  
  // Auto-sync on login
  useEffect(() => {
    if (isLoggedIn) {
      const lastSync = localStorage.getItem('lastSearchHistorySync');
      const shouldSync = !lastSync || 
        (new Date() - new Date(lastSync)) > 5 * 60 * 1000; // 5 minutes
      
      if (shouldSync) {
        syncToServer();
      }
    }
  }, [isLoggedIn, syncToServer]);
  
  return {
    isLoggedIn,
    isSyncing,
    lastSyncTime,
    syncToServer,
    fetchFromServer,
    addSearch,
    clearAll
  };
}
